import apiClient from '../../utils/axiosConfig';
import type {
  QuoteDetail,
  QuoteCreateRequest,
  QuotesListResponse,
  AddNoteRequest,
  QuoteNote,
  QuoteOutput,
  RaterFile,
  PowerPointTemplate,
  PEOGroup,
  ReinsurancePartner,
  User,
} from './types';
import { QUOTES_ENDPOINTS } from './constants';

export const quotesService = {
  async getQuotes(params?: {
    search?: string;
    status?: string;
    created_by?: string | number;
    page?: number;
    page_size?: number;
  }): Promise<QuotesListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.created_by) queryParams.append('created_by', params.created_by.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const url = `${QUOTES_ENDPOINTS.LIST}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<QuotesListResponse>(url);
    return response.data;
  },

  async getCreatedByUsers(): Promise<User[]> {
    const response = await apiClient.get<User[]>(QUOTES_ENDPOINTS.CREATED_BY_USERS);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },

  async getQuoteDetail(id: number): Promise<QuoteDetail> {
    const response = await apiClient.get<QuoteDetail>(QUOTES_ENDPOINTS.DETAIL(id));
    return response.data;
  },

  async createQuote(data: QuoteCreateRequest): Promise<QuoteDetail> {
    const { census_file, ...createData } = data;
    
    const jsonData: Record<string, unknown> = {
      prospect_name: createData.prospect_name,
      hubspot_company_id: createData.hubspot_company_id ?? null,
      quote_name: createData.quote_name,
      quote_type: createData.quote_type,
      distribution_channel: createData.distribution_channel,
      peo_group: createData.peo_group ?? null,
      policy_state: createData.policy_state,
      policy_effective_date: createData.policy_effective_date,
      status: createData.status || 'drafted',
      employee_count: createData.employee_count || 0,
      version: createData.version || 1,
      rater_file: createData.rater_file,
      power_point_template: createData.power_point_template ?? null,
      sic_code: createData.sic_code || '',
      for_profit_business: createData.for_profit_business || 'yes',
      source_quote_id: createData.source_quote_id,
    };

    if (createData.insurance_plan) {
      jsonData.insurance_plan = createData.insurance_plan;
    }

    if (createData.handbook_plan) {
      jsonData.handbook_plan = createData.handbook_plan;
    }

    const response = await apiClient.post<QuoteDetail>(QUOTES_ENDPOINTS.CREATE, jsonData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const quote = response.data;

    if (census_file) {
      const formData = new FormData();
      formData.append('census_file', census_file);

      const updatedResponse = await apiClient.patch<QuoteDetail>(
        QUOTES_ENDPOINTS.UPDATE(quote.id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return updatedResponse.data;
    }

    return quote;
  },

  async updateQuote(id: number, data: Partial<QuoteCreateRequest>): Promise<QuoteDetail> {
    const { insurance_plan, handbook_plan, census_file, ...restData } = data;

    if (census_file) {
      const formData = new FormData();

      Object.entries(restData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      formData.append('census_file', census_file);

      if (insurance_plan) {
        formData.append('insurance_plan', JSON.stringify(insurance_plan));
      }

      if (handbook_plan) {
        formData.append('handbook_plan', JSON.stringify(handbook_plan));
      }

      const response = await apiClient.patch<QuoteDetail>(QUOTES_ENDPOINTS.UPDATE(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    }

    const jsonData: Record<string, unknown> = { ...restData };
    
    if (restData.rater_file !== undefined) {
      jsonData.rater_file = restData.rater_file;
    }
    
    if (insurance_plan) {
      jsonData.insurance_plan = insurance_plan;
    }

    if (handbook_plan) {
      jsonData.handbook_plan = handbook_plan;
    }

    const response = await apiClient.patch<QuoteDetail>(QUOTES_ENDPOINTS.UPDATE(id), jsonData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  async deleteQuote(id: number): Promise<void> {
    await apiClient.delete(QUOTES_ENDPOINTS.DELETE(id));
  },

  async addNote(quoteId: number, note: AddNoteRequest): Promise<QuoteNote> {
    const response = await apiClient.post<QuoteNote>(QUOTES_ENDPOINTS.ADD_NOTE(quoteId), note);
    return response.data;
  },

  async updateQuoteOutput(
    quoteId: number,
    outputId: number,
    data: Partial<Omit<QuoteOutput, 'id'>>
  ): Promise<QuoteOutput> {
    const response = await apiClient.patch<QuoteOutput>(
      QUOTES_ENDPOINTS.UPDATE_OUTPUT(quoteId, outputId),
      data,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  },

  async createNewVersion(
    id: number,
    selectedOutputIds: number[]
  ): Promise<{ quote: QuoteDetail; analysis_file_url?: string }> {
    const response = await apiClient.post<{ quote: QuoteDetail; analysis_file_url?: string }>(
      QUOTES_ENDPOINTS.CREATE_NEW_VERSION(id),
      { selected_output_ids: selectedOutputIds }
    );
    return response.data;
  },

  async generateAnalysisFile(
    quoteId: number,
    selectedOutputIds: number[]
  ): Promise<{ message: string; task_id: string; quote_id: number }> {
    const response = await apiClient.post<{ message: string; task_id: string; quote_id: number }>(
      QUOTES_ENDPOINTS.GENERATE_ANALYSIS_FILE(quoteId),
      { selected_output_ids: selectedOutputIds }
    );
    return response.data;
  },

  async getRaterFiles(params?: {
    is_active?: boolean;
    search?: string;
  }): Promise<RaterFile[]> {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) {
      queryParams.append('is_active', params.is_active.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }

    const url = `${QUOTES_ENDPOINTS.RATER_FILES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<RaterFile[]>(url);
    const data = response.data;
    
    if (!Array.isArray(data)) {
      return [];
    }
    
    return data;
  },

  async getPowerPointTemplates(): Promise<PowerPointTemplate[]> {
    const response = await apiClient.get<PowerPointTemplate[]>(QUOTES_ENDPOINTS.POWER_POINT_TEMPLATES);
    const data = response.data;

    if (!Array.isArray(data)) {
      return [];
    }

    return data;
  },

  async getPeoGroups(): Promise<PEOGroup[]> {
    const response = await apiClient.get<PEOGroup[]>(QUOTES_ENDPOINTS.PEO_GROUPS);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },

  async getReinsurancePartners(): Promise<ReinsurancePartner[]> {
    const response = await apiClient.get<ReinsurancePartner[]>(
      QUOTES_ENDPOINTS.REINSURANCE_PARTNERS,
    );
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },

  async syncPowerPointPresentationFromOneDrive(
    quoteId: number
  ): Promise<{ message: string; task_id: string; quote_id: number }> {
    const response = await apiClient.post<{ message: string; task_id: string; quote_id: number }>(
      QUOTES_ENDPOINTS.SYNC_POWER_POINT_PRESENTATION_FROM_ONEDRIVE(quoteId)
    );
    return response.data;
  },

  async downloadPowerPointPresentation(
    quoteId: number
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get<Blob>(
      QUOTES_ENDPOINTS.DOWNLOAD_POWER_POINT_PRESENTATION(quoteId),
      { responseType: 'blob' }
    );

    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)/i);
    const filename = filenameMatch?.[1]?.replace(/"/g, '') || 'quote-presentation.pptx';

    return {
      blob: response.data,
      filename: decodeURIComponent(filename),
    };
  },
};

